<script setup lang="ts">
import { ref, onMounted } from 'vue'
import { X } from 'lucide-vue-next'
import PasswordInput from '@/components/common/PasswordInput.vue'
import ErrorMessage from '@/components/common/ErrorMessage.vue'
import AppLogo from '@/components/common/AppLogo.vue'
import { useConfig } from '@/composables/useConfig'
import { useAuthStore } from '@99fun-agent/stores'
import { useMessage } from 'naive-ui'
import { useI18n } from 'vue-i18n'
// 获取配置
const { config } = useConfig()
const { error, success } = useMessage()
const { t } = useI18n()
// 定义组件的 Props 和 Emits，以便与父级组件交互
const props = defineProps<{
  // 模拟：从父级传入当前的账号名，显示在页面上
  username: string
}>()

const authStore = useAuthStore()

const emit = defineEmits(['back', 'submitSuccess'])

const oldPassword = ref('')
const newPassword = ref('')
const confirmPassword = ref('')

const oldPasswordInputRef = ref<HTMLInputElement | null>(null)
// const error = ref('')

onMounted(() => {
  // 保持：进入页面自动聚焦到第一个输入框
  oldPasswordInputRef.value?.focus()
})

const handleSubmit = async (e: Event) => {
  e.preventDefault()

  if (!oldPassword.value) {
    error(t('old_password_ph'))
    return
  }
  if (!newPassword.value) {
    error(t('new_password_ph'))
    return
  }

  if (!confirmPassword.value) {
    error(t('confirm_password_ph'))
    return
  }

  if (newPassword.value !== confirmPassword.value) {
    error(t('confirm_password_not_same'))
  }

  const r = await authStore.reqModifyPassword({
    originPassword: oldPassword.value,
    newPassword: newPassword.value,
  })

  if (r.isSuccess) {
    oldPassword.value = ''
    newPassword.value = ''
    confirmPassword.value = ''
    success(t('operation_success'))
    emit('submitSuccess')
  }
}
</script>

<template>
  <div
    class="min-h-screen w-full flex items-center justify-center p-6 bg-gradient-to-br from-[#F2F6FF] via-[#FBF4FF] to-[#FFF2F8] relative font-sans overflow-hidden"
  >
    <div class="absolute top-8 right-8 z-50">
      <button
        @click="emit('back')"
        class="flex items-center justify-center w-10 h-10 bg-white border border-gray-100 rounded-full shadow-sm hover:shadow-md transition-all active:scale-90"
      >
        <X :size="18" class="text-gray-300" />
      </button>
    </div>

    <div
      class="w-full max-w-[350px] bg-white pt-12 pb-10 px-8 rounded-[110px] shadow-[0_10px_20px_rgba(0,0,0,0.08),0_30px_60px_rgba(0,0,0,0.05)] border border-white flex flex-col items-center relative z-10 animate-fade-in"
    >
      <!-- 使用 AppLogo 组件，图标模式 -->
      <AppLogo
        :logo-url="config.logoUrl"
        :logo-text="config.logoText"
        :title="config.title"
        mode="default"
        class="mb-8.5"
      />

      <div class="text-center mb-9.6 space-y-0 flex flex-col items-center">
        <div
          class="bg-gradient-to-r from-[#4A7DFF] via-[#A86DFF] to-[#FF45BF] bg-clip-text text-transparent"
        >
          <h1 class="text-[33.6px] font-light leading-[1.05] tracking-tight">
            {{ $t('edit_pwd') }}
          </h1>
          <!-- <h1 class="text-[33.6px] font-light leading-[1.05] tracking-tight">password</h1> -->
        </div>
        <p class="text-xs text-gray-400 mt-2">
          {{ $t('account') }}: <span class="font-bold text-gray-600">{{ props.username }}</span>
        </p>
      </div>

      <!-- <ErrorMessage :message="error" /> -->

      <form @submit="handleSubmit" class="w-full px-2">
        <div class="space-y-3.5 mb-8">
          <PasswordInput
            v-model="oldPassword"
            :label="$t('old_password')"
            :placeholder="$t('old_password_ph')"
            :input-ref="oldPasswordInputRef"
          />

          <PasswordInput
            v-model="newPassword"
            :label="$t('new_password')"
            :placeholder="$t('new_password_ph')"
            autocomplete="new-password"
          />

          <PasswordInput
            v-model="confirmPassword"
            :label="$t('confirm_password')"
            :placeholder="$t('confirm_password_ph')"
            autocomplete="new-password"
          />
        </div>

        <button
          type="submit"
          class="w-full py-3 bg-gradient-to-r from-[#4A7DFF] via-[#A86DFF] to-[#FF45BF] text-white font-black text-[13px] rounded-full shadow-[0_10px_35px_rgba(142,109,255,0.3)] active:scale-[0.96] transition-all duration-300"
        >
          {{ $t('submit') }}
        </button>
      </form>

      <!-- <div class="mt-4">
        <p class="text-[9px] text-gray-300 font-black tracking-tight uppercase">
          Security Settings
        </p>
      </div> -->
    </div>
  </div>
</template>

<style scoped>
/* 1. 复用：Login 的输入框聚焦逻辑 */
input:focus {
  border-color: rgba(142, 109, 255, 0.3) !important;
  box-shadow: 0 10px 25px rgba(142, 109, 255, 0.05) !important;
  outline: none !important;
}

/* 2. 复用：LOGO 的描边逻辑 */
.gradient-border-mask {
  background: linear-gradient(135deg, #1e4fd9 0%, #8e6dff 50%, #e65f00 100%);
  -webkit-mask:
    linear-gradient(#fff 0 0) content-box,
    linear-gradient(#fff 0 0);
  -webkit-mask-composite: xor;
  mask-composite: exclude;
}

/* 3. 新增：简单的入场动画 */
.animate-fade-in {
  animation: fadeIn 0.5s ease-out;
}

@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}
</style>
